const DROPDOWN_MAX_WIDTH_PX = 1440; // 90rem
const VIEWPORT_EDGE_GUTTER_PX = 30; // matches $grid-gutters
const DROPDOWN_LEFT_OFFSET_PX = 20;

const measureDropdownContentWidth = (dropdown: HTMLElement): number => {
    dropdown.style.width = 'max-content';
    dropdown.style.maxWidth = `${DROPDOWN_MAX_WIDTH_PX}px`;

    return dropdown.scrollWidth || dropdown.offsetWidth;
};

const HEADER_MOBILE_MEDIA_QUERY = '(max-width: 79.938em)'; // matches $bp-header-max

const setupNestedMenuInstance = (nestedMenu: HTMLElement): void => {
    const header = document.getElementById('site-header');
    const mobileNavToggle = document.querySelector('.main-nav-toggle') as HTMLElement | null;
    const isMobile = window.matchMedia(HEADER_MOBILE_MEDIA_QUERY);
    const menuButtons = nestedMenu.querySelectorAll<HTMLElement>('[data-toggle="overlay"]');
    const menuClosers = nestedMenu.querySelectorAll<HTMLElement>('[data-toggle-close]');

    const adjustDropdownPosition = (toggle: HTMLElement): void => {
        const targetId = toggle.dataset.target;

        if (!targetId) {
            return;
        }

        const dropdown = document.getElementById(targetId);

        if (!dropdown) {
            return;
        }

        dropdown.style.removeProperty('left');
        dropdown.style.removeProperty('width');
        dropdown.style.removeProperty('max-width');

        if (isMobile.matches || !toggle.classList.contains('active')) {
            return;
        }

        const item = toggle.closest('.nested-menu__item') as HTMLElement | null;

        if (!item) {
            return;
        }

        const navRect = nestedMenu.getBoundingClientRect();
        const itemRect = item.getBoundingClientRect();
        const viewportLeft = VIEWPORT_EDGE_GUTTER_PX;
        const viewportRight = window.innerWidth - VIEWPORT_EDGE_GUTTER_PX;
        const viewportWidth = viewportRight - viewportLeft;

        let leftViewport = itemRect.left - DROPDOWN_LEFT_OFFSET_PX;

        dropdown.style.left = `${leftViewport - navRect.left}px`;
        const contentWidth = measureDropdownContentWidth(dropdown);

        if (contentWidth <= 0) {
            return;
        }

        const naturalWidth = Math.min(contentWidth, DROPDOWN_MAX_WIDTH_PX);
        const toggleRect = toggle.getBoundingClientRect();
        const toggleCenter = toggleRect.left + toggleRect.width / 2;

        if (leftViewport + naturalWidth > viewportRight) {
            leftViewport = toggleCenter - naturalWidth / 2;
        }

        if (leftViewport + naturalWidth > viewportRight) {
            leftViewport = viewportRight - naturalWidth;
        }

        if (leftViewport < viewportLeft) {
            leftViewport = viewportLeft;
        }

        const availableWidth = Math.min(viewportWidth, DROPDOWN_MAX_WIDTH_PX);
        const finalWidth = Math.min(naturalWidth, viewportRight - leftViewport, availableWidth);
        const leftOffset = leftViewport - navRect.left;

        dropdown.style.left = `${leftOffset}px`;
        dropdown.style.maxWidth = `${finalWidth}px`;
        dropdown.style.width = `${finalWidth}px`;
    };

    const adjustActiveDropdownPosition = (): void => {
        const activeToggle = nestedMenu.querySelector<HTMLElement>(
            '.nested-menu__link--toggle.active'
        );

        if (activeToggle) {
            adjustDropdownPosition(activeToggle);
        }
    };

    const resetMenu = (viewport: MediaQueryListEvent | MediaQueryList): void => {
        if (!viewport.matches) {
            if (mobileNavToggle?.classList.contains('active')) {
                mobileNavToggle.click();
            }
        } else {
            document.body.click();
        }

        nestedMenu
            .querySelectorAll<HTMLElement>('.nested-menu__dropdown')
            .forEach((dropdown) => {
                dropdown.style.removeProperty('left');
                dropdown.style.removeProperty('width');
                dropdown.style.removeProperty('max-width');
            });
    };

    const loader = (): void => {
        nestedMenu.classList.add('loading');

        window.setTimeout(() => {
            nestedMenu.classList.remove('loading');
        }, 100);
    };

    const closeSubMenu = (e: Event): void => {
        const target = e.target as HTMLElement;
        const toggleCloseValue = target.dataset.toggleClose;
        const activeToggle = document.querySelector(
            `[data-target="${toggleCloseValue}"].active`
        ) as HTMLElement | null;

        // Only remove active when the hamburger toggle itself has closed,
        // not when merely closing a sub-menu while the nav is still open.
        if (header?.classList.contains('active') && !mobileNavToggle?.classList.contains('active')) {
            header.classList.remove('active');
        }

        if (toggleCloseValue) {
            const dropdown = document.getElementById(toggleCloseValue);

            dropdown?.style.removeProperty('left');
            dropdown?.style.removeProperty('width');
            dropdown?.style.removeProperty('max-width');
        }

        loader();

        if (activeToggle) {
            activeToggle.click();
            activeToggle.focus();
        }
    };

    const focusOnClose = (e: Event): void => {
        const target = e.target as HTMLElement;
        const buttonTarget = document.getElementById(target.dataset.target!);
        const subMenuCloseBtn = buttonTarget?.querySelector<HTMLElement>('[data-toggle-close]');

        subMenuCloseBtn?.focus();
    };

    menuClosers.forEach((closer) => {
        closer.addEventListener('click', closeSubMenu);
    });

    menuButtons.forEach((btn) => {
        btn.addEventListener('click', focusOnClose);
        btn.addEventListener('click', () => {
            window.setTimeout(() => {
                adjustDropdownPosition(btn);
            }, 0);
            window.setTimeout(() => {
                adjustDropdownPosition(btn);
            }, 220);
        });
    });

    isMobile.addEventListener('change', (viewport: MediaQueryListEvent) => {
        resetMenu(viewport);
    });

    window.addEventListener('resize', adjustActiveDropdownPosition);

    mobileNavToggle?.addEventListener('click', () => {
        // toggles.ts fires first and updates the active class on the toggle,
        // so by the time this runs we can read the new state.
        if (mobileNavToggle.classList.contains('active')) {
            header?.classList.add('active');
        } else {
            header?.classList.remove('active');

            // When the hamburger closes, also close any open nested submenus so
            // their overlay classes, active classes, and inline display styles are
            // fully reset before the next hamburger open.
            nestedMenu.querySelectorAll<HTMLElement>('.nested-menu__link--toggle.active')
                .forEach((toggle) => toggle.click());
        }
    });
};

export const setupNestedMenu = (): void => {
    const menus = document.querySelectorAll<HTMLElement>('.nested-menu');

    menus.forEach((menu) => {
        setupNestedMenuInstance(menu);
    });
};

setupNestedMenu();
